import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, interval } from 'rxjs';
import { map, catchError, tap, switchMap, takeWhile, finalize, filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ImportJobDto, ImportJobStatusDto, ImportHistoryDto } from '../../models/import-job.dto';

export interface UploadProgressEvent {
  type: 'upload' | 'processing';
  percentage: number;
  loaded?: number;
  total?: number;
  message?: string;
}

export interface ExcelImportConfig {
  endpoint: string;
  maxFileSize: number; // in bytes
  supportedFormats: string[];
  retryAttempts: number;
  pollingInterval: number; // milliseconds
}

export interface LMValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  headerMapping: { [key: string]: number };
}

export interface LMPreviewResult {
  filename: string;
  headers: string[];
  rows: any[][];
  totalRows: number;
  sampleRows: any[][];
  validation: LMValidationResult;
}

@Injectable({
  providedIn: 'root'
})
export class ExcelImportService {
  private apiUrl = `${environment.apiUrl}/ExcelImport`;
  
  private config: ExcelImportConfig = {
    endpoint: '/upload',
    maxFileSize: 50 * 1024 * 1024, // 50MB
    supportedFormats: ['.xlsx', '.xls', '.csv'],
    retryAttempts: 3,
    pollingInterval: 2000 // 2 seconds
  };
  
  // Subject to emit upload progress
  private progressSubject = new BehaviorSubject<UploadProgressEvent | null>(null);
  public progress$ = this.progressSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Upload Excel file and start import job
   */
  uploadExcelFile(
    file: File, 
    documentType?: string,
    additionalParams?: { [key: string]: any }
  ): Observable<ImportJobDto> {
    // Validate file
    const validationError = this.validateFile(file);
    if (validationError) {
      return throwError(() => new Error(validationError));
    }

    // Prepare form data
    const formData = new FormData();
    formData.append('file', file);
    
    if (documentType) {
      formData.append('documentType', documentType);
    }
    
    // Add additional parameters
    if (additionalParams) {
      Object.keys(additionalParams).forEach(key => {
        if (additionalParams[key] !== null && additionalParams[key] !== undefined) {
          formData.append(key, additionalParams[key].toString());
        }
      });
    }

    // Set up headers (let browser set Content-Type for multipart/form-data)
    const headers = new HttpHeaders();

    // Upload with progress tracking
    return this.http.post<ImportJobDto>(`${this.apiUrl}${this.config.endpoint}`, formData, {
      headers,
      reportProgress: true,
      observe: 'events'
    }).pipe(
      map(event => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            if (event.total) {
              const percentage = Math.round(100 * event.loaded / event.total);
              this.progressSubject.next({
                type: 'upload',
                percentage,
                loaded: event.loaded,
                total: event.total,
                message: `Uploading ${file.name}...`
              });
            }
            return null;
            
          case HttpEventType.Response:
            this.progressSubject.next({
              type: 'upload',
              percentage: 100,
              message: 'Upload complete, starting processing...'
            });
            return event.body as ImportJobDto;
            
          default:
            return null;
        }
      }),
      filter((result): result is ImportJobDto => result !== null),
      catchError(this.handleError.bind(this)),
      finalize(() => {
        // Keep progress at 100% briefly before clearing
        setTimeout(() => {
          this.progressSubject.next(null);
        }, 1000);
      })
    ) as Observable<ImportJobDto>;
  }

  /**
   * Get status of import job with optional polling
   */
  getJobStatus(jobId: string, pollUntilComplete = false): Observable<ImportJobStatusDto> {
    const statusUrl = `${this.apiUrl}/status/${jobId}`;
    
    const getStatus = () => this.http.get<ImportJobStatusDto>(statusUrl).pipe(
      tap(status => {
        // Emit processing progress
        if (status.progress) {
          this.progressSubject.next({
            type: 'processing',
            percentage: status.progress.percentage,
            message: status.progress.message || `Processing: ${status.progress.stage}`
          });
        }
      }),
      catchError(this.handleError.bind(this))
    );

    if (!pollUntilComplete) {
      return getStatus();
    }

    // Poll until job is complete
    return interval(this.config.pollingInterval).pipe(
      switchMap(() => getStatus()),
      takeWhile(status => 
        status.status === 'pending' || status.status === 'processing', 
        true // Include the final emission
      )
    );
  }

  /**
   * Cancel import job
   */
  cancelJob(jobId: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.apiUrl}/cancel/${jobId}`, {}).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Get import job details
   */
  getJobDetails(jobId: string): Observable<ImportJobDto> {
    return this.http.get<ImportJobDto>(`${this.apiUrl}/job/${jobId}`).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Get import history for current user
   */
  getImportHistory(
    page = 1, 
    pageSize = 20, 
    documentType?: string
  ): Observable<{ items: ImportHistoryDto[]; totalCount: number; totalPages: number }> {
    let params = `?page=${page}&pageSize=${pageSize}`;
    if (documentType) {
      params += `&documentType=${encodeURIComponent(documentType)}`;
    }

    return this.http.get<{ items: ImportHistoryDto[]; totalCount: number; totalPages: number }>(
      `${this.apiUrl}/history${params}`
    ).pipe(
      catchError((error: any) => this.handleError(error))
    );
  }

  /**
   * Download processed results
   */
  downloadResults(jobId: string, format: 'xlsx' | 'csv' = 'xlsx'): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/download/${jobId}`, {
      params: { format },
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Download error report
   */
  downloadErrorReport(jobId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/errors/${jobId}`, {
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Get template file for specific document type
   */
  downloadTemplate(documentType: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/template/${documentType}`, {
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Retry failed import job
   */
  retryJob(jobId: string): Observable<ImportJobDto> {
    return this.http.post<ImportJobDto>(`${this.apiUrl}/retry/${jobId}`, {}).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Upload file with retry mechanism
   */
  uploadWithRetry(
    file: File, 
    documentType?: string,
    additionalParams?: { [key: string]: any },
    maxRetries = this.config.retryAttempts
  ): Observable<ImportJobDto> {
    let attempt = 0;
    
    const attemptUpload = (): Observable<ImportJobDto> => {
      attempt++;
      return this.uploadExcelFile(file, documentType, additionalParams).pipe(
        catchError(error => {
          if (attempt < maxRetries && this.isRetryableError(error)) {
            console.warn(`Upload attempt ${attempt} failed, retrying...`, error);
            return attemptUpload();
          }
          return throwError(() => error);
        })
      );
    };

    return attemptUpload();
  }

  /**
   * Validate file before upload
   */
  private validateFile(file: File): string | null {
    // Check file size
    if (file.size > this.config.maxFileSize) {
      const maxSizeMB = Math.round(this.config.maxFileSize / (1024 * 1024));
      return `File size exceeds ${maxSizeMB}MB limit`;
    }

    // Check file extension
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!this.config.supportedFormats.includes(fileExtension)) {
      return `Unsupported file format. Supported formats: ${this.config.supportedFormats.join(', ')}`;
    }

    // Check if file is empty
    if (file.size === 0) {
      return 'Cannot upload empty file';
    }

    return null;
  }

  /**
   * Determine if error is retryable
   */
  private isRetryableError(error: any): boolean {
    if (error instanceof HttpErrorResponse) {
      // Retry on network errors and server errors (5xx)
      return error.status === 0 || (error.status >= 500 && error.status < 600);
    }
    return false;
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred during the Excel import operation';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Network error: ${error.error.message}`;
    } else {
      // Server-side error
      switch (error.status) {
        case 400:
          errorMessage = error.error?.message || 'Invalid file or request parameters';
          break;
        case 401:
          errorMessage = 'Authentication required. Please log in again.';
          break;
        case 403:
          errorMessage = 'Access denied. You do not have permission to import files.';
          break;
        case 413:
          errorMessage = 'File size too large. Please select a smaller file.';
          break;
        case 415:
          errorMessage = 'Unsupported file format. Please upload an Excel file.';
          break;
        case 422:
          errorMessage = error.error?.message || 'File validation failed. Please check your data format.';
          break;
        case 429:
          errorMessage = 'Too many requests. Please wait before uploading again.';
          break;
        case 500:
          errorMessage = 'Server error occurred. Please try again later.';
          break;
        case 503:
          errorMessage = 'Service temporarily unavailable. Please try again later.';
          break;
        default:
          errorMessage = error.error?.message || `Server error (${error.status})`;
      }
    }
    
    console.error('Excel Import Error:', error);
    return throwError(() => new Error(errorMessage));
  }

  /**
   * Clear progress subject
   */
  clearProgress(): void {
    this.progressSubject.next(null);
  }

  /**
   * Get current configuration
   */
  getConfig(): ExcelImportConfig {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<ExcelImportConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get file extension validation message
   */
  getSupportedFormatsMessage(): string {
    return `Supported formats: ${this.config.supportedFormats.join(', ')}`;
  }

  /**
   * Preview LM Excel file without uploading
   */
  previewLMExcel(file: File): Observable<LMPreviewResult> {
    // Validate file first
    const validationError = this.validateFile(file);
    if (validationError) {
      return throwError(() => new Error(validationError));
    }

    return new Observable<LMPreviewResult>(observer => {
      const reader = new FileReader();
      
      reader.onload = (e: any) => {
        try {
          // Using XLSX library to read the file
          const XLSX = (window as any).XLSX;
          if (!XLSX) {
            observer.error(new Error('XLSX library not loaded'));
            return;
          }

          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convert to array of arrays with header row
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length < 2) {
            observer.error(new Error('File must contain at least a header row and one data row'));
            return;
          }
          
          const headers = jsonData[0] as string[];
          const rows = jsonData.slice(1).map((row: any) => row as any[]);
          
          // Validate LM format
          const validation = this.validateLMFormat(headers);
          
          // Get sample rows (first 5 for preview)
          const sampleRows = rows.slice(0, 5);
          
          const previewResult: LMPreviewResult = {
            filename: file.name,
            headers,
            rows,
            totalRows: rows.length,
            sampleRows,
            validation
          };
          
          observer.next(previewResult);
          observer.complete();
          
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to read Excel file';
          observer.error(new Error(`File parsing error: ${errorMessage}`));
        }
      };
      
      reader.onerror = () => {
        observer.error(new Error('Failed to read file'));
      };
      
      reader.readAsArrayBuffer(file);
    }).pipe(
      catchError((error: any) => {
        console.error('LM Preview Error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Validate LM file headers
   */
  validateLMHeaders(file: File): Observable<LMValidationResult> {
    return this.previewLMExcel(file).pipe(
      map(preview => preview.validation),
      catchError(error => {
        const result: LMValidationResult = {
          isValid: false,
          errors: [error.message || 'Validation failed'],
          warnings: [],
          headerMapping: {}
        };
        return throwError(() => result);
      })
    );
  }

  /**
   * Upload LM file with pre-alert data
   */
  importLMFileWithPreAlert(
    file: File,
    preAlertData: any,
    additionalParams?: { [key: string]: any }
  ): Observable<ImportJobDto> {
    // Validate file
    const validationError = this.validateFile(file);
    if (validationError) {
      return throwError(() => new Error(validationError));
    }

    // Prepare form data to match backend expectations
    const formData = new FormData();
    formData.append('file', file);  // Backend ExcelImportController expects 'file' parameter
    
    // Add pre-alert input fields with correct camelCase field names to match backend expectations
    if (preAlertData) {
      // Debug logging to track what we're sending
      console.log('EXCEL_UPLOAD_DEBUG: Sending pre-alert data to backend:', preAlertData);
      
      // Map frontend camelCase field names to backend expected form field names
      formData.append('LadingBill', preAlertData.ladingBill || '');
      formData.append('departureLocation', preAlertData.departureLocation || '');
      formData.append('arrivalLocation', preAlertData.arrivalLocation || '');
      formData.append('etd', preAlertData.etd || '');
      formData.append('eta', preAlertData.eta || '');
      formData.append('weight', preAlertData.weight?.toString() || '0');
      formData.append('chargeableWeight', preAlertData.chargeableWeight?.toString() || '');
      formData.append('volumetricWeight', preAlertData.volumetricWeight?.toString() || '');
      formData.append('transportCompany', preAlertData.transportCompany || preAlertData.transportCompanyName || '');
      formData.append('vesselNumber', preAlertData.vesselNumber || preAlertData.vesselNo || '');
      formData.append('previousLadingBill', preAlertData.previousLadingBill || '');
      formData.append('actionType', '1'); // 1 = Add (creating new orders from Excel)
      formData.append('pieces', preAlertData.pieces?.toString() || '0');
      
      // Debug: Log each form field being sent
      console.log('EXCEL_UPLOAD_DEBUG: FormData fields being sent:');
      for (const [key, value] of formData.entries()) {
        console.log(`  - ${key}: '${value}'`);
      }
    }
    
    // Add additional parameters
    if (additionalParams) {
      Object.keys(additionalParams).forEach(key => {
        if (additionalParams[key] !== null && additionalParams[key] !== undefined) {
          formData.append(key, additionalParams[key].toString());
        }
      });
    }

    // Use dedicated LM endpoint that matches the backend
    const lmEndpoint = `${this.apiUrl}/import-lm-file`;
    const headers = new HttpHeaders();

    return this.http.post<ImportJobDto>(lmEndpoint, formData, {
      headers,
      reportProgress: true,
      observe: 'events'
    }).pipe(
      map(event => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            if (event.total) {
              const percentage = Math.round(100 * event.loaded / event.total);
              this.progressSubject.next({
                type: 'upload',
                percentage,
                loaded: event.loaded,
                total: event.total,
                message: `Uploading LM file with pre-alert...`
              });
            }
            return null;
            
          case HttpEventType.Response:
            this.progressSubject.next({
              type: 'processing',
              percentage: 100,
              message: 'LM file uploaded successfully, processing with pre-alert data...'
            });
            
            const responseBody = event.body as ImportJobDto;
            
            // Debug: Log the complete response for troubleshooting
            console.log('EXCEL_UPLOAD_DEBUG: Complete API response received:', responseBody);
            
            // If successful and has resultData with preAlertData, log the pre-alert fields
            if (responseBody && responseBody.resultData && (responseBody.resultData as any).preAlertData) {
              const preAlertData = (responseBody.resultData as any).preAlertData;
              console.log('EXCEL_UPLOAD_DEBUG: Pre-alert data returned from backend:', preAlertData);
              console.log('EXCEL_UPLOAD_DEBUG: Pre-alert fields validation:');
              console.log(`  - ladingBill: '${preAlertData.ladingBill || 'MISSING'}'`);
              console.log(`  - departureLocation: '${preAlertData.departureLocation || 'MISSING'}'`);
              console.log(`  - arrivalLocation: '${preAlertData.arrivalLocation || 'MISSING'}'`);
              console.log(`  - etd: '${preAlertData.etd || 'MISSING'}'`);
              console.log(`  - eta: '${preAlertData.eta || 'MISSING'}'`);
              console.log(`  - weight: '${preAlertData.weight || 'MISSING'}'`);
              console.log(`  - transportCompanyName: '${preAlertData.transportCompanyName || 'MISSING'}'`);
              console.log(`  - vesselNo: '${preAlertData.vesselNo || 'MISSING'}'`);
              console.log(`  - pieces: '${preAlertData.pieces || 'MISSING'}'`);
            } else {
              console.warn('EXCEL_UPLOAD_DEBUG: No preAlertData found in API response or response is in error state');
            }
            
            return responseBody;
            
          default:
            return null;
        }
      }),
      filter((result): result is ImportJobDto => result !== null),
      catchError(this.handleError.bind(this))
    ) as Observable<ImportJobDto>;
  }

  /**
   * Validate LM format headers and structure
   */
  private validateLMFormat(headers: string[]): LMValidationResult {
    const result: LMValidationResult = {
      isValid: true,
      errors: [],
      warnings: [],
      headerMapping: {}
    };

    // Required LM columns (case-insensitive matching)
    const requiredColumns = [
      { key: 'orderId', patterns: ['order id', 'orderid', 'order_id', 'order no', 'orderno'] },
      { key: 'trackingNumber', patterns: ['tracking number', 'trackingnumber', 'tracking_number', 'tracking', 'tracking_no'] },
      { key: 'weight', patterns: ['weight', 'weight_kg', 'weight (kg)', 'gross weight'] },
      { key: 'recipient', patterns: ['recipient', 'customer', 'customer name', 'receiver', 'recipient name'] }
    ];

    const normalizedHeaders = headers.map((h, index) => ({
      original: h,
      normalized: h?.toLowerCase().trim().replace(/[^a-z0-9]/g, '') || '',
      index
    }));

    // Check for required columns
    for (const required of requiredColumns) {
      let found = false;
      
      for (const pattern of required.patterns) {
        const normalizedPattern = pattern.toLowerCase().replace(/[^a-z0-9]/g, '');
        const matchedHeader = normalizedHeaders.find(h => 
          h.normalized === normalizedPattern || 
          h.normalized.includes(normalizedPattern) ||
          normalizedPattern.includes(h.normalized)
        );
        
        if (matchedHeader) {
          result.headerMapping[required.key] = matchedHeader.index;
          found = true;
          break;
        }
      }
      
      if (!found) {
        result.errors.push(`Missing required column: ${required.patterns[0]} (or similar)`);
        result.isValid = false;
      }
    }

    // Optional columns that are commonly found
    const optionalColumns = [
      { key: 'dimensions', patterns: ['dimensions', 'size', 'length x width x height'] },
      { key: 'address', patterns: ['address', 'delivery address', 'recipient address'] },
      { key: 'phone', patterns: ['phone', 'mobile', 'contact number', 'phone number'] },
      { key: 'remarks', patterns: ['remarks', 'notes', 'special instructions', 'comment'] }
    ];

    // Map optional columns if found
    for (const optional of optionalColumns) {
      for (const pattern of optional.patterns) {
        const normalizedPattern = pattern.toLowerCase().replace(/[^a-z0-9]/g, '');
        const matchedHeader = normalizedHeaders.find(h => 
          h.normalized === normalizedPattern || 
          h.normalized.includes(normalizedPattern)
        );
        
        if (matchedHeader) {
          result.headerMapping[optional.key] = matchedHeader.index;
          break;
        }
      }
    }

    // Check for suspicious or problematic headers
    const suspiciousHeaders = normalizedHeaders.filter(h => 
      h.normalized === '' || 
      h.normalized.length < 2 ||
      /^column\d+$/.test(h.normalized)
    );

    if (suspiciousHeaders.length > 0) {
      result.warnings.push(`Found ${suspiciousHeaders.length} suspicious or empty column header(s)`);
    }

    // Check for duplicate headers
    const duplicates = normalizedHeaders.filter((h, index) => 
      normalizedHeaders.findIndex(h2 => h2.normalized === h.normalized && h2.normalized !== '') !== index
    );

    if (duplicates.length > 0) {
      result.warnings.push('Found duplicate column headers');
    }

    return result;
  }

  /**
   * Get LM template data for download
   */
  getLMTemplate(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/template/lm`, {
      responseType: 'blob'
    }).pipe(
      catchError(error => {
        // If backend template not available, generate client-side template
        console.warn('Backend LM template not available, generating client-side template');
        return this.generateLMTemplate();
      })
    );
  }

  /**
   * Generate LM template on client side as fallback
   */
  private generateLMTemplate(): Observable<Blob> {
    try {
      const XLSX = (window as any).XLSX;
      if (!XLSX) {
        return throwError(() => new Error('XLSX library not available for template generation'));
      }

      // Template headers
      const headers = [
        'Order ID',
        'Tracking Number', 
        'Weight (kg)',
        'Recipient',
        'Address',
        'Phone Number',
        'Remarks'
      ];

      // Sample data
      const sampleData = [
        ['ORD-2025-001', 'TN123456789', 2.5, 'John Doe', '123 Main St, Manila, Philippines', '+63 912 345 6789', 'Fragile item'],
        ['ORD-2025-002', 'TN123456790', 1.8, 'Jane Smith', '456 Oak Ave, Cebu, Philippines', '+63 918 765 4321', 'Standard delivery'],
        ['ORD-2025-003', 'TN123456791', 0.9, 'Bob Wilson', '789 Pine St, Davao, Philippines', '+63 917 111 2222', 'Express delivery']
      ];

      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Create data sheet
      const ws = XLSX.utils.aoa_to_sheet([headers, ...sampleData]);
      XLSX.utils.book_append_sheet(wb, ws, 'LM Data');
      
      // Create instructions sheet
      const instructionsData = [
        ['Last Mile (LM) Delivery Template'],
        [''],
        ['REQUIRED COLUMNS:'],
        ['- Order ID: Unique identifier for each order'],
        ['- Tracking Number: Package tracking identifier'],
        ['- Weight (kg): Package weight in kilograms'],
        ['- Recipient: Name of the person receiving the package'],
        [''],
        ['OPTIONAL COLUMNS:'],
        ['- Address: Delivery address'],
        ['- Phone Number: Contact number for delivery'],
        ['- Remarks: Special delivery instructions'],
        [''],
        ['INSTRUCTIONS:'],
        ['1. Fill in the required information in the "LM Data" sheet'],
        ['2. Do not modify the column headers'],
        ['3. Ensure all required fields are completed'],
        ['4. Save the file and upload through the LM upload interface'],
        ['5. You will be prompted to enter pre-alert information after file selection']
      ];
      
      const instructionsWs = XLSX.utils.aoa_to_sheet(instructionsData);
      XLSX.utils.book_append_sheet(wb, instructionsWs, 'Instructions');
      
      // Convert to blob
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      return new Observable<Blob>(observer => {
        observer.next(blob);
        observer.complete();
      });
      
    } catch (error) {
      return throwError(() => new Error(`Failed to generate LM template: ${error}`));
    }
  }
}

